import {useState} from 'react';
import {DataTableSortStatus} from 'mantine-datatable';
// The comparator is SHARED (FNP-912) — nulls-last, numeric-vs-string and the
// empty-string rule live in one place rather than being re-derived per table.
import {sortRecords} from '@flexinet/ui-components';

import {useHostedZonesInfiniteQuery} from './useHostedZonesQuery';
import {useHostedZonesSearch} from './useHostedZonesSearch';
import {HostedZone} from '../types';

// The DEFAULT is a per-entity judgement and stays local; HOW two values compare
// does not (that is `sortRecords`).
//
// `name` rather than a creation timestamp: a Route 53 hosted zone as this API
// returns it has NO created/updated field at all, so the usual "newest first"
// default is not available. Of what remains, `name` is the only key that is both
// NON-NULL (`String!`) and immutable — `recordSetCount` moves under the reader
// as records are added, and `comment` / `privateZone` are nullable and would
// degrade to arrival order. Alphabetical is also what an operator scanning a
// list of zones expects.
export const DEFAULT_SORT_STATUS: DataTableSortStatus<HostedZone> = {
    columnAccessor: 'name',
    direction: 'asc',
};

export const matchesKeyword = (item: HostedZone, needle: string): boolean => {
    if (!needle) {
        return true;
    }
    const haystack = [
        item.name,
        item.id,
        item.accountId,
        item.comment,
    ];
    return haystack.some(value => typeof value === 'string' && value.toLowerCase()
                                                                    .includes(needle));
};

export const useHostedZones = (parentDomainName?: string) => {

    const query = useHostedZonesInfiniteQuery(parentDomainName);

    // Component state, not the module-scoped store: sort order is how ONE
    // operator is looking at the table right now, unlike the keyword, which is
    // shared across sibling components (FNP-815).
    const [sortStatus, setSortStatus] =
        useState<DataTableSortStatus<HostedZone>>(DEFAULT_SORT_STATUS);

    const {keyword} = useHostedZonesSearch();

    const items: HostedZone[] = (query.data?.pages?.flatMap(
        page => (page && 'items' in page) ? page.items : [],
    ) ?? []).filter(Boolean);

    const needle = keyword.trim()
                          .toLowerCase();

    // ⚠️ Sorting and keyword filtering are CLIENT-SIDE, over the pages fetched so
    // far. Neither can reach rows the browser has not loaded. That is a property
    // of token pagination, not an oversight — and it is why the footer states a
    // LOADED count rather than a total.
    const visibleItems = sortRecords(
        items.filter(item => matchesKeyword(item, needle)),
        sortStatus,
    );

    return {
        // The loaded set BEFORE keyword filtering — the window the footer's
        // "N of M loaded" claim is measured against.
        items,
        visibleItems,
        sortStatus,
        setSortStatus,
        loading: query.isLoading,
        isError: query.isError,
        isFetchingNextPage: query.isFetchingNextPage,
        nextToken: query.hasNextPage ? 'has-more' : null,
        fetchRecords: () => {
            if (query.hasNextPage && !query.isFetchingNextPage) {
                query.fetchNextPage();
            }
        },
        reload: () => query.refetch(),
    };
};
